import React, { useState } from 'react';
import { RuleSection, RemoveButton } from './Rules.styles';
import { FormContainer, InputBodyContainer } from '../../NewGame.styles';
import ItemSelectInput from '../../custom-inputs/ItemSelectInput';
import CustomSelect from '../../custom-inputs/CustomSelect';
import CustomToggleSwitch from '../../custom-inputs/CustomToggleSwitch';
import NewGameState from '../../_types/NewGameState';
import CustomTextInput from '../../custom-inputs/CustomTextInput';
import { SectionTitle } from '../../GameFormComponents';
import { GamifyToast } from 'application/common/CustomToasts';

interface Props {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  kpis: { label: string; value: string; rule: any }[];
}

interface KPI {
  label: string;
  value: string;
  rule: any;
}

function NumberInput(props: any) {
  const [text, setText] = useState(props.value);
  return (
    <CustomTextInput
      label={props.label}
      onChange={(newValue) => {
        // Regex to check if newValue is a number or not (includes decimals, using parseFloat prevents reliable usage on isNaN for checking this)
        const isNumber = /^-?\d*\.?\d*$/.test(newValue);
        if (newValue === '' || isNumber) {
          setText(newValue);
          props.onChangeText(newValue);
        }
      }}
      value={text}
    />
  );
}

const renderMultipleKpiInputs = (
  newGameState: NewGameState,
  onChangeNewGameState: (newGameState: NewGameState) => void,
  kpis: { label: string; value: string; rule: any }[],
  normalizedKPIs: any,
  newPointsPerAction: string,
  setNewPointsPerAction: React.Dispatch<React.SetStateAction<string>>,
  selectedRole: string,
  setSelectedRole: React.Dispatch<React.SetStateAction<string>>
): React.JSX.Element => {
  // Use missionRules as the primary data structure
  // This is the field expected by the backend
  const missionRules = newGameState.missionRules || [];

  console.log(
    'renderMultipleKpiInputs - current missionRules:',
    JSON.stringify(missionRules)
  );
  console.log(
    'renderMultipleKpiInputs - current KPIRuleID:',
    newGameState.KPIRuleID
  );

  const addKpiRule = () => {
    console.log('Add KPI button clicked');
    console.log('Current state:', {
      KPIRuleID: newGameState.KPIRuleID,
      pointsPerAction: newPointsPerAction,
      role: selectedRole,
      currentRules: missionRules.length,
    });

    // If we don't have any KPIs selected in state, try to get one from the dropdown
    const selectedKpiId =
      newGameState.KPIRuleID || (kpis.length > 0 ? kpis[0].value : '');

    if (!selectedKpiId) {
      alert('No KPIs available to add');
      return;
    }

    // If missionRules is empty, we may need to create the first entry
    if (missionRules.length === 0) {
      const pointsValue = parseFloat(newPointsPerAction) || 1.0;

      // Create first rule
      const newRule = {
        id: selectedKpiId,
        multiplier: pointsValue,
        name:
          newGameState.KPIRuleName ||
          normalizedKPIs[selectedKpiId]?.label ||
          '',
        role: selectedRole,
        sequence: 1,
      };

      console.log('Adding first KPI rule:', JSON.stringify(newRule));

      // Update state with the new rule
      onChangeNewGameState({
        ...newGameState,
        missionRules: [newRule],
        rules: [newRule],
        KPIRuleID: newRule.id,
        KPIRuleName: newRule.name,
      });

      // Reset inputs
      setNewPointsPerAction('1.0');
      setSelectedRole('contestant');

      // Find next available KPI and select it
      const usedKpiIds = [newRule.id];
      const nextAvailableKpi = kpis.find(
        (kpi) => !usedKpiIds.includes(kpi.value)
      );

      if (nextAvailableKpi) {
        onChangeNewGameState({
          ...newGameState,
          missionRules: [newRule],
          rules: [newRule],
          KPIRuleID: nextAvailableKpi.value,
          KPIRuleName: nextAvailableKpi.label,
        });
      }

      return;
    }

    // Make sure we don't add duplicate KPI rules
    const existingRule = missionRules.find((rule) => rule.id === selectedKpiId);
    if (existingRule) {
      // Find an available KPI that hasn't been used yet
      const usedKpiIds = missionRules.map((rule) => rule.id);
      const availableKpis = kpis.filter(
        (kpi) => !usedKpiIds.includes(kpi.value)
      );

      if (availableKpis.length === 0) {
        alert('All KPIs have already been added');
        return;
      }

      // Automatically select the first available KPI
      const suggestedKpi = availableKpis[0];
      console.log('Selecting available KPI:', suggestedKpi.label);

      // Select the new KPI in the state
      onChangeNewGameState({
        ...newGameState,
        KPIRuleID: suggestedKpi.value,
        KPIRuleName: suggestedKpi.label,
      });

      return;
    }

    // Get current values
    const pointsValue = parseFloat(newPointsPerAction) || 1.0;

    // Create new rule
    const newRule = {
      id: selectedKpiId,
      multiplier: pointsValue,
      name: normalizedKPIs[selectedKpiId]?.label || '',
      role: selectedRole,
      sequence: missionRules.length + 1,
    };

    // Create a completely new array with the new rule
    const updatedRules = [...missionRules, newRule];

    console.log('Adding KPI rule:', {
      selectedKpiId,
      pointsValue,
      role: selectedRole,
      currentRules: missionRules.length,
      newRules: updatedRules.length,
      updatedRules: JSON.stringify(updatedRules),
    });

    // Always ensure the main game's KPIRuleID is set from the first rule
    // This is critical for validation to pass
    const firstRule = updatedRules[0];

    // Update state with the new rules
    const newState = {
      ...newGameState,
      missionRules: updatedRules,
      rules: updatedRules,
      // Always ensure main KPIRuleID is set from the first rule
      KPIRuleID: firstRule.id,
      KPIRuleName: firstRule.name,
    };

    // Select another available KPI for the next addition
    const usedKpiIds = updatedRules.map((rule) => rule.id);
    const nextAvailableKpi = kpis.find(
      (kpi) => !usedKpiIds.includes(kpi.value)
    );

    if (nextAvailableKpi) {
      newState.KPIRuleID = nextAvailableKpi.value;
      newState.KPIRuleName = nextAvailableKpi.label;
    } else {
      // If no more available KPIs, ensure it's still set to the first rule's KPI
      newState.KPIRuleID = firstRule.id;
      newState.KPIRuleName = firstRule.name;
    }

    onChangeNewGameState(newState);

    // Reset the points per action input value
    setNewPointsPerAction('1.0');
    // Reset role to default
    setSelectedRole('contestant');
  };

  const renderKPIList = () => {
    console.log(
      'renderKPIList called - missionRules count:',
      missionRules.length
    );

    // If there are no mission rules but we're in multiple KPI mode, create a default KPI
    if (missionRules.length === 0 && newGameState.useMultipleKPIs) {
      // Add a default KPI using the first available KPI
      if (kpis && kpis.length > 0) {
        console.log('Adding default KPI to empty missionRules list');

        // Create a default rule with the first KPI
        const defaultRule = {
          id: kpis[0].value,
          name: kpis[0].label,
          multiplier: 1.0,
          role: 'contestant',
          sequence: 1,
        };

        // Update the state with the default rule
        setTimeout(() => {
          onChangeNewGameState({
            ...newGameState,
            missionRules: [defaultRule],
            rules: [defaultRule],
            KPIRuleID: defaultRule.id,
            KPIRuleName: defaultRule.name,
          });
        }, 0);

        // Return a loading placeholder
        return [
          <RuleSection key="default-kpi-loading">
            <div style={{ padding: '16px', textAlign: 'center' }}>
              Loading KPI editor...
            </div>
          </RuleSection>,
        ];
      }
    }

    return missionRules.map((_missionRule, ruleNum) => {
      console.log(`Rendering KPI #${ruleNum}:`, JSON.stringify(_missionRule));

      // Determine if this is the last rule in the list
      const isLastRule = ruleNum === missionRules.length - 1;

      return (
        <RuleSection key={`kpi-rule-${_missionRule.id}-${ruleNum}`}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <CustomSelect
                label={`KPI`}
                value={_missionRule.id}
                onChange={(newValue: any) => {
                  // Check if this KPI is already in use by another rule
                  const duplicateIndex = missionRules.findIndex(
                    (rule, idx) => rule.id === newValue && idx !== ruleNum
                  );

                  if (duplicateIndex >= 0) {
                    alert('This KPI is already used in another rule');
                    return;
                  }

                  // Create a copy of the existing rules
                  const _missionRules = [...missionRules];

                  // Update just this rule's KPI
                  _missionRules[ruleNum] = {
                    ..._missionRules[ruleNum],
                    id: newValue,
                    name: normalizedKPIs[newValue]?.label || '',
                  };

                  console.log(
                    'Updating KPI for rule',
                    ruleNum,
                    'to:',
                    newValue
                  );
                  console.log(
                    'Updated missionRules:',
                    JSON.stringify(_missionRules)
                  );

                  // If this is the first rule, update KPIRuleID and KPIRuleName
                  const updatedState = {
                    ...newGameState,
                    missionRules: _missionRules,
                    rules: _missionRules,
                  };

                  // Always update KPIRuleID to be the first rule in multiple KPI mode
                  if (
                    newGameState.useMultipleKPIs &&
                    _missionRules.length > 0
                  ) {
                    updatedState.KPIRuleID = _missionRules[0].id;
                    updatedState.KPIRuleName = _missionRules[0].name;
                  } else if (ruleNum === 0) {
                    updatedState.KPIRuleID = newValue;
                    updatedState.KPIRuleName =
                      normalizedKPIs[newValue]?.label || '';
                  }

                  // Update state with the modified rules
                  onChangeNewGameState(updatedState);
                }}
                items={kpis}
              />
            </div>
            <div style={{ flex: 1 }}>
              <NumberInput
                label={`Points Per Action*`}
                value={_missionRule.multiplier?.toString()}
                onChangeText={(newValue: string) => {
                  // Create a copy of the existing rules
                  const _missionRules = [...missionRules];

                  // Update just this rule's multiplier
                  _missionRules[ruleNum] = {
                    ..._missionRules[ruleNum],
                    multiplier: parseFloat(newValue) || 0,
                  };

                  console.log(
                    'Updating multiplier for rule',
                    ruleNum,
                    'to:',
                    newValue
                  );
                  console.log(
                    'Updated missionRules:',
                    JSON.stringify(_missionRules)
                  );

                  onChangeNewGameState({
                    ...newGameState,
                    missionRules: _missionRules,
                    rules: _missionRules,
                  });
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <CustomSelect
              label="User's Role"
              value={_missionRule.role}
              onChange={(newValue) => {
                // Create a copy of the existing rules
                const _missionRules = [...missionRules];

                // Update just this rule's role
                _missionRules[ruleNum] = {
                  ..._missionRules[ruleNum],
                  role: newValue,
                };

                console.log('Updating role for rule', ruleNum, 'to:', newValue);
                console.log(
                  'Updated missionRules:',
                  JSON.stringify(_missionRules)
                );

                onChangeNewGameState({
                  ...newGameState,
                  missionRules: _missionRules,
                  rules: _missionRules,
                });
              }}
              items={[
                {
                  label: 'Anyone',
                  value: 'contestant',
                },
                {
                  label: 'Setter',
                  value: 'setter',
                },
                {
                  label: 'Closer',
                  value: 'closer',
                },
              ]}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '8px',
            }}
          >
            <RemoveButton
              onClick={() => {
                console.log('Removing rule at index:', ruleNum);
                console.log(
                  'Current rules before removal:',
                  JSON.stringify(missionRules)
                );

                const _missionRules = [...missionRules];
                _missionRules.splice(ruleNum, 1);

                console.log(
                  'Rules after removal:',
                  JSON.stringify(_missionRules)
                );

                // Update KPIRuleID when removing a rule
                const updatedState = {
                  ...newGameState,
                  missionRules: _missionRules,
                  rules: _missionRules,
                };

                // If there are remaining rules, set KPIRuleID to the first one
                if (_missionRules.length > 0) {
                  updatedState.KPIRuleID = _missionRules[0].id;
                  updatedState.KPIRuleName = _missionRules[0].name;
                } else {
                  updatedState.KPIRuleID = '';
                  updatedState.KPIRuleName = '';
                }

                onChangeNewGameState(updatedState);
              }}
            >
              Remove
            </RemoveButton>
          </div>

          {/* Only show the Add KPI button on the last rule */}
          {isLastRule && (
            <div style={{ marginBottom: '16px', marginTop: '16px' }}>
              <div
                onClick={addKpiRule}
                style={{
                  color: '#FF6A00',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                + Add KPI
              </div>
            </div>
          )}
        </RuleSection>
      );
    });
  };

  // Return the complete UI with existing KPIs and the form to add new ones
  return <>{renderKPIList()}</>;
};

const RulesView: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
  kpis,
}) => {
  // Needed for accessing the rule on a KPI when selecting a rule.
  const normalizeKPIs = (kpis: KPI[]): Record<string, KPI> => {
    return kpis.reduce((acc, kpi) => {
      acc[kpi.value] = kpi;
      return acc;
    }, {} as Record<string, KPI>);
  };

  const normalizedKPIs = normalizeKPIs(kpis);
  const [showMultipleKpis, setShowMultipleKpis] = useState(
    newGameState.useMultipleKPIs || false
  );
  const [newPointsPerAction, setNewPointsPerAction] = useState('1.0');
  const [selectedRole, setSelectedRole] = useState('contestant');

  // Log state when component renders
  console.log(
    'RulesView rendering - useMultipleKPIs:',
    newGameState.useMultipleKPIs
  );
  console.log(
    'RulesView rendering - missionRules:',
    JSON.stringify(newGameState.missionRules)
  );
  console.log('RulesView rendering - KPIRuleID:', newGameState.KPIRuleID);

  return (
    <FormContainer>
      <SectionTitle>Rules</SectionTitle>
      <InputBodyContainer>
        <CustomToggleSwitch
          label="Use Multiple KPIs"
          value={showMultipleKpis}
          onChange={(newValue: any) => {
            console.log('Toggle switch changed to:', newValue);
            console.log('Current game state:', {
              useMultipleKPIs: newGameState.useMultipleKPIs,
              missionRules: JSON.stringify(newGameState.missionRules || []),
              KPIRuleID: newGameState.KPIRuleID,
            });

            // To match mobile logic: reset existing missionRules and alert user when turning on
            if (newValue) {
              // When switching to multiple KPIs, reset the state and prepare for adding new ones

              // If we have a single KPI rule already, convert it to the first mission rule
              const initialRules = [];
              if (newGameState.KPIRuleID) {
                console.log(
                  'Converting single KPI to first mission rule:',
                  newGameState.KPIRuleID
                );

                // Find if we already have mission rule for this KPI
                const existingRule = (newGameState.missionRules || []).find(
                  (rule) => rule.id === newGameState.KPIRuleID
                );

                if (existingRule) {
                  // Use the existing rule
                  initialRules.push(existingRule);
                } else {
                  // Create a new rule
                  initialRules.push({
                    id: newGameState.KPIRuleID,
                    name:
                      newGameState.KPIRuleName ||
                      normalizedKPIs[newGameState.KPIRuleID]?.label ||
                      '',
                    multiplier: 1.0, // Default multiplier
                    role: 'contestant', // Default role
                    sequence: 1,
                  });
                }
              } else if (kpis && kpis.length > 0) {
                // If no KPI is selected but we have available KPIs, create a default rule with the first KPI
                initialRules.push({
                  id: kpis[0].value,
                  name: kpis[0].label,
                  multiplier: 1.0, // Default multiplier
                  role: 'contestant', // Default role
                  sequence: 1,
                });
              }

              console.log(
                'Initial rules for multiple KPI mode:',
                JSON.stringify(initialRules)
              );

              // In multiple KPI mode, always set the KPIRuleID from the first rule (if available)
              const updatedState = {
                ...newGameState,
                useMultipleKPIs: newValue,
                missionRules: initialRules,
                rules: initialRules,
              };

              // Set KPIRuleID from the first rule if available, otherwise clear it
              if (initialRules.length > 0) {
                // Always set KPIRuleID to ensure validation passes
                const firstRule = initialRules[0];
                updatedState.KPIRuleID = firstRule.id;
                updatedState.KPIRuleName = firstRule.name;
              } else {
                // If no rules, try to set it from the first available KPI
                if (kpis && kpis.length > 0) {
                  updatedState.KPIRuleID = kpis[0].value;
                  updatedState.KPIRuleName = kpis[0].label;
                } else {
                  updatedState.KPIRuleID = '';
                  updatedState.KPIRuleName = '';
                }
              }

              onChangeNewGameState(updatedState);

              // Reset the form inputs
              setNewPointsPerAction('1.0');
              setSelectedRole('contestant');

              GamifyToast.success(
                "If your game has multiple KPI's, you will be required to define the point value of each KPI in the game."
              );
            } else if (
              newGameState.missionRules &&
              newGameState.missionRules.length > 0
            ) {
              // When switching from multiple to single KPI, use the first mission rule
              const firstRule = newGameState.missionRules[0];
              console.log(
                'Switching to single KPI mode using first rule:',
                JSON.stringify(firstRule)
              );

              onChangeNewGameState({
                ...newGameState,
                useMultipleKPIs: newValue,
                // Always ensure KPIRuleID is set from the first rule when toggling
                KPIRuleID: firstRule.id,
                KPIRuleName: firstRule.name,
                // Keep the mission rules in case we switch back
              });
            } else {
              console.log('No KPIs available, just toggling mode');
              onChangeNewGameState({
                ...newGameState,
                useMultipleKPIs: newValue,
              });
            }

            setShowMultipleKpis(newValue);
          }}
        />
        {showMultipleKpis ? (
          renderMultipleKpiInputs(
            newGameState,
            onChangeNewGameState,
            kpis,
            normalizedKPIs,
            newPointsPerAction,
            setNewPointsPerAction,
            selectedRole,
            setSelectedRole
          )
        ) : (
          <>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '0px' }}>
              <div style={{ flex: 1 }}>
                <CustomSelect
                  label="KPI*"
                  value={newGameState.KPIRuleID}
                  onChange={(newValue: any) => {
                    onChangeNewGameState({
                      ...newGameState,
                      KPIRuleID: newValue,
                      KPIRuleName: normalizedKPIs[newValue]?.label || '',
                    });
                  }}
                  items={kpis}
                />
              </div>
              <div style={{ flex: 1 }}>
                <NumberInput
                  label="Points Per Action*"
                  value={(() => {
                    if (newGameState.missionRules && newGameState.KPIRuleID) {
                      const rule = newGameState.missionRules.find(
                        (rule) => rule.id === newGameState.KPIRuleID
                      );
                      return rule?.multiplier?.toString() || '1.0';
                    }
                    return '1.0';
                  })()}
                  onChangeText={(newValue: string) => {
                    // Create or update the mission rule with the new multiplier value
                    let _missionRules = newGameState.missionRules || [];
                    const ruleIndex = _missionRules.findIndex(
                      (rule) => rule.id === newGameState.KPIRuleID
                    );

                    if (ruleIndex >= 0) {
                      // Update existing rule
                      _missionRules[ruleIndex] = {
                        ..._missionRules[ruleIndex],
                        multiplier: parseFloat(newValue),
                      };
                    } else if (newGameState.KPIRuleID) {
                      // Create new rule
                      _missionRules = [
                        {
                          id: newGameState.KPIRuleID,
                          name:
                            normalizedKPIs[newGameState.KPIRuleID]?.label || '',
                          multiplier: parseFloat(newValue),
                          role: 'contestant',
                          sequence: 1,
                        },
                      ];
                    }

                    onChangeNewGameState({
                      ...newGameState,
                      missionRules: _missionRules,
                      rules: _missionRules,
                    });
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '0px' }}>
              <CustomSelect
                label="User's Role"
                value={(() => {
                  // Find the rule for the current KPI in missionRules array
                  if (newGameState.missionRules && newGameState.KPIRuleID) {
                    const rule = newGameState.missionRules.find(
                      (rule) => rule.id === newGameState.KPIRuleID
                    );
                    return rule?.role || 'contestant';
                  }
                  return 'contestant';
                })()}
                onChange={(newValue: any) => {
                  // Create or update the single mission rule using missionRules array
                  let _missionRules = newGameState.missionRules || [];

                  // Check if we already have a rule for this KPI
                  const ruleIndex = _missionRules.findIndex(
                    (rule) => rule.id === newGameState.KPIRuleID
                  );

                  if (ruleIndex >= 0) {
                    // Update existing rule
                    _missionRules[ruleIndex] = {
                      ..._missionRules[ruleIndex],
                      role: newValue,
                    };
                  } else if (newGameState.KPIRuleID) {
                    // Create new rule for the selected KPI
                    _missionRules = [
                      {
                        id: newGameState.KPIRuleID,
                        name:
                          normalizedKPIs[newGameState.KPIRuleID]?.label || '',
                        multiplier: 1.0,
                        role: newValue,
                        sequence: 1,
                      },
                    ];
                  }

                  onChangeNewGameState({
                    ...newGameState,
                    missionRules: _missionRules, // Primary array for backend
                    rules: _missionRules, // Keep rules in sync with missionRules for compatibility
                  });
                }}
                items={[
                  {
                    label: 'Anyone',
                    value: 'contestant',
                  },
                  {
                    label: 'Setter',
                    value: 'setter',
                  },
                  {
                    label: 'Closer',
                    value: 'closer',
                  },
                ]}
              />
            </div>
          </>
        )}
        <CustomSelect
          label="Tie Breaker KPI"
          value={newGameState.tieKPIRuleID}
          onChange={(newValue: any) => {
            onChangeNewGameState({ ...newGameState, tieKPIRuleID: newValue });
          }}
          items={kpis}
        />
        <ItemSelectInput
          label="Goal"
          selectedItem={
            newGameState.isActionCountCustom
              ? 'custom'
              : newGameState.actionCount
          }
          onSelect={(newSelectedItem: { label: string; value: any } | null) => {
            if (!newSelectedItem) {
              // Deselect - clear the goal
              onChangeNewGameState({
                ...newGameState,
                isActionCountCustom: false,
                actionCount: '',
              });
            } else if (newSelectedItem.value === 'custom') {
              onChangeNewGameState({
                ...newGameState,
                isActionCountCustom: true,
                actionCount: '',
              });
            } else {
              onChangeNewGameState({
                ...newGameState,
                isActionCountCustom: false,
                actionCount: newSelectedItem.value,
              });
            }
          }}
          items={[
            { label: '10', value: '10' },
            { label: '25', value: '25' },
            { label: '50', value: '50' },
            { label: 'Custom', value: 'custom' },
          ]}
          numColumns={4}
          allowDeselect={true}
        />
        {newGameState.isActionCountCustom && (
          <CustomTextInput
            label="Custom Goal"
            placeholder="E.g. 15"
            value={newGameState.actionCount}
            onChange={(newValue) => {
              onChangeNewGameState({ ...newGameState, actionCount: newValue });
            }}
          />
        )}
      </InputBodyContainer>
    </FormContainer>
  );
};

export default RulesView;
